/**
 * Порядковий діфф двох версій промпту.
 *
 * Версійність промпту існує рівно заради того, щоб різницю було видно: блок шаблону
 * закріплений за конкретною версією, і питання «що змінилось у v3 проти v2» — це питання
 * «чому сітка почне писати інакше». Доти, доки історія була переліком номерів, відповідь на
 * нього доводилося тримати в голові.
 *
 * Порядково, а не послівно: тіло промпту — це інструкції рядками, і зміна одного слова в
 * рядку майже завжди означає зміну сенсу всього рядка. Послівний діфф тут показував би менше.
 */

export type DiffOp = 'same' | 'added' | 'removed';

export interface DiffLine {
  op: DiffOp;
  text: string;
}

/**
 * Найдовша спільна підпослідовність — щоб вставлений посередині рядок не зсував решту в
 * «змінено». Наївне попарне порівняння показувало б увесь хвіст як переписаний.
 */
export function lineDiff(before: string, after: string): DiffLine[] {
  const a = before.split('\n');
  const b = after.split('\n');

  /**
   * Одновимірна таблиця з ручним індексом замість масиву масивів: доступ до `lcs[i][j]` через
   * необовʼязковий елемент вимагав би або `!` на кожному зверненні, або перевірки, яка ніколи
   * не спрацює. Плоский масив із `at(index)` і явним дефолтом чесніший за обидва.
   */
  const width = b.length + 1;
  const lcs = new Int32Array((a.length + 1) * width);
  const cell = (i: number, j: number): number => lcs[i * width + j] ?? 0;

  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i * width + j] =
        a[i] === b[j] ? cell(i + 1, j + 1) + 1 : Math.max(cell(i + 1, j), cell(i, j + 1));
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    const left = a[i] ?? '';
    const right = b[j] ?? '';
    if (left === right) {
      out.push({ op: 'same', text: left });
      i += 1;
      j += 1;
    } else if (cell(i + 1, j) >= cell(i, j + 1)) {
      out.push({ op: 'removed', text: left });
      i += 1;
    } else {
      out.push({ op: 'added', text: right });
      j += 1;
    }
  }
  while (i < a.length) {
    out.push({ op: 'removed', text: a[i] ?? '' });
    i += 1;
  }
  while (j < b.length) {
    out.push({ op: 'added', text: b[j] ?? '' });
    j += 1;
  }
  return out;
}

/** Скільки рядків додано й скільки прибрано — заголовок діффу без розгортання. */
export function diffSummary(lines: DiffLine[]): { added: number; removed: number } {
  return {
    added: lines.filter((line) => line.op === 'added').length,
    removed: lines.filter((line) => line.op === 'removed').length,
  };
}
